import React from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 4rem 0;
  background:
    radial-gradient(circle at top left, rgba(15, 118, 110, 0.08), transparent 34%),
    linear-gradient(180deg, #f4efe5 0%, #fffdf9 100%);
`;

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 1.6rem;
`;

const Header = styled.div`
  max-width: 760px;
  margin-bottom: 2.4rem;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #a16207;
`;

const Title = styled.h2`
  margin: 0 0 0.9rem;
  font-size: 2.5rem;
  line-height: 1.02;
  color: #0f172a;

  @media (max-width: 640px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #475569;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.4rem;
  margin-bottom: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  padding: 1.6rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
`;

const CardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.9rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: ${props => props.$background};
  color: ${props => props.$color};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardTitle = styled.h3`
  margin: 0 0 0.8rem;
  font-size: 1.55rem;
  color: #0f172a;
`;

const CardBody = styled.p`
  margin: 0 0 1rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #475569;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.7rem;
`;

const Bullet = styled.li`
  padding-left: 1rem;
  position: relative;
  color: #1e293b;
  line-height: 1.5;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62rem;
    width: 0.38rem;
    height: 0.38rem;
    border-radius: 999px;
    background: ${props => props.$dot};
  }
`;

const MethodologyLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #0f766e;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    color: #115e59;
  }
`;

const requestTypeCards = [
  {
    badge: 'External anchor',
    badgeBackground: 'rgba(37, 99, 235, 0.12)',
    badgeColor: '#1d4ed8',
    dot: '#2563eb',
    title: 'Binary forecasts',
    body:
      'Use external-market links or internal oracles to collect probability forecasts on a resolvable question.',
    bullets: [
      'Typical anchor: external_market_link to Polymarket or another public venue.',
      'Required answer fields: probability, confidence, rationale, and evidence bundle.',
      'Best for questions where final truth can be checked cleanly later.',
    ],
  },
  {
    badge: 'Checkpointed',
    badgeBackground: 'rgba(15, 118, 110, 0.12)',
    badgeColor: '#0f766e',
    dot: '#0f766e',
    title: 'Research to forecast',
    body:
      'Ask providers to decompose a hard question into gradeable pieces before synthesizing the final answer.',
    bullets: [
      'Typical checkpoint template: decomposed subquestion pack.',
      'Supports early settlement once intermediate artifacts are graded.',
      'Fits long-horizon work where the final reward lands far in the future.',
    ],
  },
  {
    badge: 'Private anchor',
    badgeBackground: 'rgba(245, 158, 11, 0.14)',
    badgeColor: '#b45309',
    dot: '#d97706',
    title: 'Private dataset review',
    body:
      'Keep proprietary KPIs private while still grading work against a reviewer group and a clear metric definition.',
    bullets: [
      'Resolution anchor specifies the data owner, reviewer group, and metric definition.',
      'Useful for internal outcome requests that cannot be posted to a public market.',
      'Supports private and public request modes with different approval thresholds.',
    ],
  },
];

const BenchmarkFamilies = () => {
  return (
    <Section id="request-types">
      <Container>
        <Header>
          <Eyebrow>Request Types</Eyebrow>
          <Title>Request types and resolution anchors</Title>
          <Subtitle>
            Start with a small set of request shapes that can be resolved cleanly. Each one
            combines a structured provider payload with a resolution anchor that explains how truth
            will be decided later.
          </Subtitle>
        </Header>

        <Grid>
          {requestTypeCards.map(card => (
            <Card key={card.title}>
              <CardBadge $background={card.badgeBackground} $color={card.badgeColor}>
                {card.badge}
              </CardBadge>
              <CardTitle>{card.title}</CardTitle>
              <CardBody>{card.body}</CardBody>
              <BulletList>
                {card.bullets.map(item => (
                  <Bullet key={item} $dot={card.dot}>
                    {item}
                  </Bullet>
                ))}
              </BulletList>
            </Card>
          ))}
        </Grid>

        <MethodologyLink href="#settlement">Explore settlement guardrails →</MethodologyLink>
      </Container>
    </Section>
  );
};

export default BenchmarkFamilies;
